import * as React from 'react';
import { Grid, Modal, Tooltip } from '@material-ui/core';
import dataList from '../../constants/dataList';
import MUIDataTable from "mui-datatables";
import Loading from '../../components/loading';
import AlertTransactionModal from './alert-transaction-modal.component';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SearchFilter from '../../components/searchFilter';
import { CommonStyles } from '../../hooks/styles';

interface AlertTransactionsProps {
	authUser?: any
	alertTransactions: any[]
	totalAlertTransactions: any
	globalRowsPerPage: any
	getAlertTransactions: (filter: any) => void
	resolveRx: (payload: any) => void
	setGlobalRowsPerPage: (numRows: any) => void
}

export const AlertTransactionsComponent: React.FC<AlertTransactionsProps> = (props) => {
	const firstRender = React.useRef(false);
	const classes = CommonStyles();
	const [modalOpen, setModalOpen] = React.useState(false)
	const [rxToEdit, setRxToEdit] = React.useState(null as any)
	const [sortOrder, setSortOrder] = React.useState(null as any)
	const [filters, setFilters] = React.useState<any>({})
	const [tableStatePersist, setTableStatePersist] = React.useState(null as any);
	const [pagination, setPageState] = React.useState({
		offset: 0,
		limit: props.globalRowsPerPage,
		page: 0
	});

	const renderIcons: any = (tableMeta: any) => {
		return (
			<div style={{ color: 'rgba(0, 0, 0, 0.54)', float: 'right' }}>
				<Tooltip title="Actions" placement="bottom-start">
					<MoreVertIcon
						onClick={(e) => {
							triggerModal(props.alertTransactions[tableMeta.rowIndex]);
						}}
						style={{ marginRight: 10, cursor: 'pointer' }}
					/>
				</Tooltip>
			</div>
		)
	}

	const columnData = [
		{
			label: "Rx Number",
			name: "rxNumber",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Refill Number",
			name: "refillNumber",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Patient Mrn",
			name: "patientMrn",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Product Id",
			name: "productId",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "NDC",
			name: "ndc",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Doctor Npi",
			name: "doctorNpi",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Invoice",
			name: "invoice",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "State",
			name: "rxState",
			change: true,
			viewColumns: true,
			display: true,
			sort: false,
			customBodyRender: ((value: any) => {
				const rxState = JSON.parse(value)
				return rxState.state
			})
		},
		{
			label: "",
			name: "",
			change: false,
			viewColumns: false,
			display: true,
			sort: false,
			customBodyRender: (value: any, tableMeta: any) => { return renderIcons(tableMeta) }
		},
	]

	const getTableColumns = () => {
		return columnData.map((column: any, index: number) => {
			return {
				label: column.label,
				name: column.name,
				options: {
					filter: true,
					display: tableStatePersist ? tableStatePersist.columns[index].display : column.display,
					change: column.change,
					sort: column.sort ?? true,
					viewColumns: column.viewColumns,
					...(column.customBodyRender) && { customBodyRender: column.customBodyRender },
				}
			}
		})
	}

	React.useEffect(() => {
		if (props.authUser && !firstRender.current) {
			fitlerAlertTransactions()
		}
		else {
			firstRender.current = false;
		}
	}, [pagination, sortOrder])

	const getFilter = (reset?: boolean) => {
		const filter: any = {
			limit: props.globalRowsPerPage,
			offset: pagination.offset,
			order: sortOrder ? [sortOrder] : ['id'],
			where: { stateId: { lt: 100 } } //filters ultimately end up here 
		}

		Object.keys(filters).forEach((filterKey: string) => {
			if (['rxNumber', 'productId'].includes(filterKey)) filter.where[filterKey] = filters[filterKey] ? {ilike: `%${filters[filterKey]}%`} : undefined
			else filter.where[filterKey] = filters[filterKey] || undefined
		})

		return filter
	}

	const fitlerAlertTransactions = (reset?: boolean) => {
		props.getAlertTransactions({ filter: getFilter(reset) })
	}

	const showLoadingMsg = () => (
		!props.authUser && props.alertTransactions.length === 0
	)

	const handleModalClose = () => {
		setModalOpen(false);
		setRxToEdit(null)
	};

	const triggerModal = (rx: any) => {
		setRxToEdit(rx);
		setModalOpen(true);
	}

	return (<div>
		{showLoadingMsg() ? <div><Loading message="" /></div> : <div>
			<div className={classes.searchWrap}>
				<Grid container>
					<Grid item xs={'auto'}>
						<SearchFilter
							config={[
								{
									label: 'Rx Number',
									name: 'rxNumber',
									type: 'text'
								},
								{
									label: 'Refill Number',
									name: 'refillNumber',
									type: 'text'
								},
								{
									label: 'Product Id',
									name: 'productId',
									type: 'text'
								}
							]}
							onFilterChange={setFilters}
							filters={filters}
							handleSearch={() => setPageState({ ...pagination, offset: 0, page: 0 })}
						/>
					</Grid>
				</Grid>
			</div>
			<div>
				<MUIDataTable
					data={props.alertTransactions}
					columns={getTableColumns()}
					options={{
						rowsPerPage: props.globalRowsPerPage,
						rowsPerPageOptions: dataList.PageLimitOptions,
						onChangeRowsPerPage: (numberOfRows: number) => {
							setPageState({ offset: 0, limit: numberOfRows, page: 0 });
							props.setGlobalRowsPerPage(numberOfRows);
						},
						filterType: "dropdown",
						responsive: "simple",
						filter: false,
						search: false,
						download: true,
						print: true,
						selectableRows: 'none',
						serverSide: true,
						page: pagination.page,
						count: props.totalAlertTransactions,
						onDownload: (buildHead: any, buildBody: any, columns: any) => {
							const filter = getFilter()
							delete filter.limit
							delete filter.offset
							const exportOptions = {
								columns: columns,
								timestampFormat: 'YYYY-MM-DD',
								fileName: 'prescription-exceptions'
							}
							props.getAlertTransactions({ filter: filter, exportOptions: exportOptions })
							return false
						},
						onColumnSortChange: (changedColumn: string, direction: string) => {
							setSortOrder(`${changedColumn} ${direction.toUpperCase()}`)
						},
						onTableChange: (tableAction: any, tableState: any) => {
							switch (tableAction) {
								case 'changePage':
									if (tableState.page > pagination.page) {
										setPageState({ offset: pagination.offset + props.globalRowsPerPage, limit: props.globalRowsPerPage, page: tableState.page });
									}
									else if (tableState.page < pagination.page) {
										setPageState({ offset: pagination.offset - props.globalRowsPerPage, limit: props.globalRowsPerPage, page: tableState.page });
									}
									break;
								case 'viewColumnsChange':
									setTableStatePersist(tableState);
									break;
							}
						},
					}}
				/>
			</div>
			{/* :
				<EmptyContent message="Change the filter criteria above to view product information." />
			} */}
			<Modal
				open={modalOpen}
				onClose={(e: any, reason: string) => {
					if (reason === 'backdropClick') return
					handleModalClose()
				}}
				aria-labelledby="simple-modal-title"
				aria-describedby="simple-modal-description"
			>
				<div>
					<AlertTransactionModal getAlertTransactions={fitlerAlertTransactions} rx={rxToEdit} resolveRx={props.resolveRx} closeModal={handleModalClose} />
				</div>
			</Modal>
		</div>}
	</div>)
}